import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** Matches theme desktop breakpoint (see base.css @media min-width: 750px). */
const STICKY_HEADER_PADDING_MIN_WIDTH_MQ = '(min-width: 750px)';

/** Extra space below the sticky header when applying gallery padding (px). */
const STICKY_HEADER_PADDING_EXTRA_PX = 30;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupStickyHeaderPadding();
  }

  #controller = new AbortController();

  /** @type {MutationObserver | null} */
  #headerStateObserver = null;

  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  #headerPaddingRaf = 0;

  disconnectedCallback() {
    this.#teardownStickyHeaderPadding();
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #setupStickyHeaderPadding() {
    if (!this.classList.contains('sticky-content')) return;

    const header = this.#findHeaderComponent();
    if (!header) return;

    const { signal } = this.#controller;

    const scheduleUpdate = () => {
      if (this.#headerPaddingRaf) return;
      this.#headerPaddingRaf = requestAnimationFrame(() => {
        this.#headerPaddingRaf = 0;
        this.#updateStickyHeaderPadding();
      });
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', scheduleUpdate, { signal });

    const desktopMql = window.matchMedia(STICKY_HEADER_PADDING_MIN_WIDTH_MQ);
    desktopMql.addEventListener('change', scheduleUpdate, { signal });

    this.#headerStateObserver = new MutationObserver(scheduleUpdate);
    this.#headerStateObserver.observe(header, {
      attributes: true,
      attributeFilter: ['data-sticky-state', 'data-animating'],
    });

    this.#headerResizeObserver = new ResizeObserver(scheduleUpdate);
    this.#headerResizeObserver.observe(header);

    scheduleUpdate();
  }

  #teardownStickyHeaderPadding() {
    this.#headerStateObserver?.disconnect();
    this.#headerStateObserver = null;
    this.#headerResizeObserver?.disconnect();
    this.#headerResizeObserver = null;
    if (this.#headerPaddingRaf) {
      cancelAnimationFrame(this.#headerPaddingRaf);
      this.#headerPaddingRaf = 0;
    }
    this.style.paddingTop = '';
  }

  /** @returns {HTMLElement | null} */
  #findHeaderComponent() {
    return document.querySelector('#header-component');
  }

  /**
   * True when the theme header is in its visible sticky state (see header.liquid / header.js).
   * @param {HTMLElement} header
   */
  #isHeaderStickyOverlayVisible(header) {
    if (!header.hasAttribute('sticky')) return false;
    if (header.dataset.stickyState !== 'active') return false;
    if (header.hasAttribute('data-animating')) return false;

    const style = getComputedStyle(header);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity) < 0.01) return false;
    return header.offsetHeight > 0;
  }

  #isSticking() {
    const style = getComputedStyle(this);
    if (style.position !== 'sticky') return false;
    const stickyTop = parseFloat(style.top);
    if (Number.isNaN(stickyTop)) return false;
    const { top } = this.getBoundingClientRect();
    return Math.abs(top - stickyTop) < 3;
  }

  #updateStickyHeaderPadding() {
    if (!this.isConnected || !this.classList.contains('sticky-content')) return;

    if (!window.matchMedia(STICKY_HEADER_PADDING_MIN_WIDTH_MQ).matches) {
      if (this.style.paddingTop !== '') this.style.paddingTop = '';
      return;
    }

    const header = this.#findHeaderComponent();
    if (!header || !this.#isSticking() || !this.#isHeaderStickyOverlayVisible(header)) {
      if (this.style.paddingTop !== '') this.style.paddingTop = '';
      return;
    }

    const next = `${header.getBoundingClientRect().height + STICKY_HEADER_PADDING_EXTRA_PX}px`;
    if (this.style.paddingTop !== next) this.style.paddingTop = next;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
